import React, { useState, useEffect } from 'react';
import { vehicleAvailabilityAPI } from '../../services/api';
import { formatDateInput } from '../../utils/date';
import '../Customers/Customers.css';

const VehicleAvailabilityPage = ({ user }) => {
  const [exceptions, setExceptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingException, setEditingException] = useState(null);
  const [formData, setFormData] = useState({
    exception_date: '',
    start_time: '',
    end_time: '',
    number_available: 3,
    reason: ''
  });
  const [formError, setFormError] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    fetchExceptions();
  }, []);

  const fetchExceptions = async (overrideStartDate = null, overrideEndDate = null) => {
    try {
      setLoading(true);
      const params = {};
      
      const effectiveStartDate = overrideStartDate !== null ? overrideStartDate : startDate;
      const effectiveEndDate = overrideEndDate !== null ? overrideEndDate : endDate;

      if (effectiveStartDate) {
        params.start_date = effectiveStartDate;
      }
      if (effectiveEndDate) {
        params.end_date = effectiveEndDate;
      }

      const data = await vehicleAvailabilityAPI.getAll(params);
      setExceptions(data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load vehicle availability exceptions');
      console.error('Error fetching exceptions:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = (e) => {
    e.preventDefault();
    fetchExceptions();
  };

  const handleClearFilters = () => {
    setStartDate('');
    setEndDate('');
    fetchExceptions();
  };

  const handleStartDateChange = (e) => {
    setStartDate(e.target.value);
  };

  const handleEndDateChange = (e) => {
    setEndDate(e.target.value);
  };

  const handlePresetClick = (preset) => {
    const targetDate = getDateForPreset(preset);
    const endDateObj = new Date(targetDate);
    endDateObj.setDate(endDateObj.getDate() + 6);
    
    const formatDateForInput = (date) => formatDateInput(date);
    
    let effectiveStartDate, effectiveEndDate;
    
    if (preset === 'today' || preset === 'tomorrow') {
      effectiveStartDate = formatDateForInput(targetDate);
      effectiveEndDate = formatDateForInput(targetDate);
    } else {
      effectiveStartDate = formatDateForInput(targetDate);
      effectiveEndDate = formatDateForInput(endDateObj);
    }
    
    setStartDate(effectiveStartDate);
    setEndDate(effectiveEndDate);
    
    fetchExceptions(effectiveStartDate, effectiveEndDate);
  };

  const getDateForPreset = (preset) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    switch (preset) {
      case 'today':
        return today;
      case 'tomorrow': {
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        return tomorrow;
      }
      case 'thisWeek': {
        const thisWeek = new Date(today);
        const dayOfWeek = thisWeek.getDay();
        const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        thisWeek.setDate(thisWeek.getDate() - diff);
        return thisWeek;
      }
      case 'lastWeek': {
        const lastWeek = new Date(today);
        const dayOfWeek = lastWeek.getDay();
        const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        lastWeek.setDate(lastWeek.getDate() - diff - 7);
        return lastWeek;
      }
      case 'nextWeek': {
        const nextWeek = new Date(today);
        const dayOfWeek = nextWeek.getDay();
        const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        nextWeek.setDate(nextWeek.getDate() - diff + 7);
        return nextWeek;
      }
      case 'twoWeeks': {
        const twoWeeks = new Date(today);
        const dayOfWeek = twoWeeks.getDay();
        const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        twoWeeks.setDate(twoWeeks.getDate() - diff + 14);
        return twoWeeks;
      }
      case 'threeWeeks': {
        const threeWeeks = new Date(today);
        const dayOfWeek = threeWeeks.getDay();
        const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        threeWeeks.setDate(threeWeeks.getDate() - diff + 21);
        return threeWeeks;
      }
      default:
        return today;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-NZ', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return '-';
    // Use regex to extract hours and minutes from various datetime formats
    const match = String(timeString).match(/(\d{2}):(\d{2})/);
    if (!match) return timeString;
    const hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2], 10);
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
  };

  const openAddModal = () => {
    setEditingException(null);
    setFormData({
      exception_date: '',
      start_time: '',
      end_time: '',
      number_available: 3,
      reason: ''
    });
    setFormError(null);
    setShowModal(true);
  };

  const openEditModal = (exception) => {
    setEditingException(exception);
    setFormData({
      exception_date: exception.exception_date,
      start_time: exception.start_time,
      end_time: exception.end_time,
      number_available: exception.number_available,
      reason: exception.reason || ''
    });
    setFormError(null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingException(null);
    setFormData({
      exception_date: '',
      start_time: '',
      end_time: '',
      number_available: 3,
      reason: ''
    });
    setFormError(null);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'number_available' ? parseInt(value, 10) || 0 : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);

    try {
      if (editingException) {
        await vehicleAvailabilityAPI.update(editingException.id, formData);
      } else {
        await vehicleAvailabilityAPI.create(formData);
      }
      closeModal();
      fetchExceptions();
    } catch (err) {
      setFormError(err.response?.data?.message || 'Failed to save exception');
    }
  };

  const handleDelete = async (id) => {
    try {
      await vehicleAvailabilityAPI.delete(id);
      setDeleteConfirm(null);
      fetchExceptions();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete exception');
    }
  };

  return (
    <div className="dashboard-container">
      <div className="dashboard-main-wrapper">
        <main className="dashboard-content">
          <div className="customer-list-container">
            <div className="customer-list-header">
              <h1>Vehicle Availability Exceptions</h1>
              <button
                className="btn btn-primary"
                onClick={openAddModal}
              >
                + Add Exception
              </button>
            </div>

            <div className="customer-search">
              <div className="filters-row">
                <div className="filter-group">
                  <label>Date From: </label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={handleStartDateChange}
                    className="filter-input"
                  />
                </div>
                <div className="filter-group">
                  <label>Date To: </label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={handleEndDateChange}
                    className="filter-input"
                  />
                </div>
                <button 
                  type="button" 
                  className="btn btn-primary"
                  onClick={() => fetchExceptions()}
                >
                  Filter
                </button>
                {(startDate || endDate) && (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleClearFilters}
                  >
                    Clear
                  </button>
                )}
              </div>
              <div className="filters-row presets-row">
                <button
                  type="button"
                  className="btn btn-sm btn-preset"
                  onClick={() => handlePresetClick('thisWeek')}
                >
                  This Week
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-preset"
                  onClick={() => handlePresetClick('lastWeek')}
                >
                  Last Week
                </button>

                <button
                  type="button"
                  className="btn btn-secondary btn-sm btn-preset"
                  onClick={() => handlePresetClick('today')}
                >
                  Today
                </button>
                <button
                  type="button"
                  className="btn btn-primary btn-sm btn-preset"
                  onClick={() => handlePresetClick('tomorrow')}
                >
                  Tomorrow
                </button>
                
                <button
                  type="button"
                  className="btn btn-sm btn-preset"
                  onClick={() => handlePresetClick('nextWeek')}
                >
                  Next Week
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-preset"
                  onClick={() => handlePresetClick('twoWeeks')}
                >
                  2 Weeks From Now
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-preset"
                  onClick={() => handlePresetClick('threeWeeks')}
                >
                  3 Weeks From Now
                </button>
              </div>
            </div>

            {error && <div className="error-message">{error}</div>}

            {loading ? (
              <div className="loading">Loading exceptions...</div>
            ) : (
              <>
                <div className="customer-table-container">
                  <table className="customer-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Start Time</th>
                        <th>End Time</th>
                        <th>Vehicles Available</th>
                        <th>Reason</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {exceptions.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="no-data">
                            No vehicle availability exceptions found
                          </td>
                        </tr>
                      ) : (
                        exceptions.map((exception) => (
                          <tr key={exception.id}>
                            <td data-label="Date">{formatDate(exception.exception_date)}</td>
                            <td data-label="Start Time">{formatTime(exception.start_time)}</td>
                            <td data-label="End Time">{formatTime(exception.end_time)}</td>
                            <td data-label="Vehicles Available">
                              <span className={`badge ${exception.number_available < 3 ? 'badge-warning' : 'badge-info'}`}>
                                {exception.number_available}
                              </span>
                            </td>
                            <td data-label="Reason">{exception.reason || '-'}</td>
                            <td data-label="Actions" className="actions-cell">
                              <button
                                className="btn btn-sm btn-secondary"
                                onClick={() => openEditModal(exception)}
                              >
                                Edit
                              </button>
                              <button
                                className="btn btn-sm btn-danger"
                                onClick={() => setDeleteConfirm(exception.id)}
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </main>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingException ? 'Edit Exception' : 'Add Vehicle Availability Exception'}</h2>
            </div>
            
            {formError && <div className="error-message">{formError}</div>}
            <div className="modal-body">
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="exception_date">Date *</label>
                  <input
                    type="date"
                    id="exception_date"
                    name="exception_date"
                    value={formData.exception_date}
                    onChange={handleFormChange}
                    required
                    className="form-input"
                  />
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="start_time">Start Time *</label>
                    <input
                      type="time"
                      id="start_time"
                      name="start_time"
                      value={formData.start_time}
                      onChange={handleFormChange}
                      required
                      className="form-input"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="end_time">End Time *</label>
                    <input
                      type="time"
                      id="end_time"
                      name="end_time"
                      value={formData.end_time}
                      onChange={handleFormChange}
                      required
                      className="form-input"
                    />
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="number_available">Vehicles Available *</label>
                  <input
                    type="number"
                    id="number_available"
                    name="number_available"
                    value={formData.number_available}
                    onChange={handleFormChange}
                    min="0"
                    max="10"
                    required
                    className="form-input"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="reason">Reason (optional)</label>
                  <input
                    type="text"
                    id="reason"
                    name="reason"
                    value={formData.reason}
                    onChange={handleFormChange}
                    placeholder="e.g., Truck breakdown, maintenance"
                    className="form-input"
                  />
                </div>
                
                <div className="form-actions">
                  <button type="button" className="btn btn-secondary" onClick={closeModal}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    {editingException ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Confirm Delete</h2>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete this vehicle availability exception?</p>
              
              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setDeleteConfirm(null)}>
                  Cancel
                </button>
                <button type="button" className="btn btn-danger" onClick={() => handleDelete(deleteConfirm)}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VehicleAvailabilityPage;
